import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api/client";
import { unwrap } from "@/lib/api/unwrap";

import type {
  FormationGroup,
  FormationGroupCreate,
  FormationGroupDetail,
  Goal,
  Issue,
  PatchedFormationGroupDetail,
  Thematic,
} from "./types";

export const formationGroupKeys = {
  all: ["formation-groups"] as const,
  list: () => [...formationGroupKeys.all, "list"] as const,
  detail: (id: string) => [...formationGroupKeys.all, "detail", id] as const,
  catalogue: () => [...formationGroupKeys.all, "catalogue"] as const,
  defaultGoals: () => [...formationGroupKeys.all, "default-goals"] as const,
};

export function useFormationGroupsQuery() {
  return useQuery({
    queryKey: formationGroupKeys.list(),
    queryFn: async () => {
      const data = await unwrap(api.GET("/api/v1/formation-groups/"));
      return data.results as FormationGroup[];
    },
  });
}

export function useFormationGroupQuery(id: string) {
  return useQuery({
    queryKey: formationGroupKeys.detail(id),
    enabled: Boolean(id),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/formation-groups/{id}/", {
          params: { path: { id } },
        }),
      ) as Promise<FormationGroupDetail>,
  });
}

export function useCreateFormationGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FormationGroupCreate) =>
      unwrap(api.POST("/api/v1/formation-groups/", { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: formationGroupKeys.all }),
  });
}

/**
 * The update hook the partner-group slice never got.
 *
 * The Django app has had a group edit view all along while the SPA had only
 * create and delete — an asymmetry the ticket explicitly says not to reproduce
 * here, so training groups carry all four operations in both apps.
 */
export function useUpdateFormationGroup(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchedFormationGroupDetail) =>
      unwrap(
        api.PATCH("/api/v1/formation-groups/{id}/", {
          params: { path: { id } },
          body,
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formationGroupKeys.detail(id) });
      qc.invalidateQueries({ queryKey: formationGroupKeys.list() });
    },
  });
}

export function useDeleteFormationGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE("/api/v1/formation-groups/{id}/", {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: formationGroupKeys.all }),
  });
}

/**
 * Open or close enrolment.
 *
 * A verb of its own rather than a PATCH on `is_open`, which the API refuses:
 * opening a group is what makes its code usable, and it should not be
 * reachable as a side effect of a save about the group's name.
 */
export function useSetEnrolmentOpen(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (open: boolean) =>
      unwrap(
        open
          ? api.POST("/api/v1/formation-groups/{id}/open/", {
              params: { path: { id: groupId } },
            })
          : api.POST("/api/v1/formation-groups/{id}/close/", {
              params: { path: { id: groupId } },
            }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formationGroupKeys.detail(groupId) });
      qc.invalidateQueries({ queryKey: formationGroupKeys.list() });
    },
  });
}

/** Replace the enrolment code; the previous value stops working immediately. */
export function useRegenerateCode(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/v1/formation-groups/{id}/regenerate-code/", {
          params: { path: { id: groupId } },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formationGroupKeys.detail(groupId) });
      qc.invalidateQueries({ queryKey: formationGroupKeys.list() });
    },
  });
}

/**
 * Empty the group for the next cohort, and replace its code.
 *
 * `useRegenerateCode` above and `useRemoveParticipant` below, done at once —
 * a cohort that has left still knows the code, so removing them while leaving
 * it working would not have ended the cohort.
 *
 * POST, not DELETE: `DELETE /formation-groups/{id}/` already deletes the group
 * itself, and these two sit one segment apart on the same resource.
 *
 * The response carries the number actually removed and the new code, both of
 * which the toast says. The count is the server's, not `participants.length` —
 * the page may be minutes old, and a concurrent removal makes the two
 * disagree.
 */
export function useResetFormationGroup(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/v1/formation-groups/{id}/reset/", {
          params: { path: { id: groupId } },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formationGroupKeys.detail(groupId) });
      qc.invalidateQueries({ queryKey: formationGroupKeys.list() });
    },
  });
}

export function useRemoveParticipant(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) =>
      unwrap(
        api.DELETE(
          "/api/v1/formation-groups/{id}/participants/{membership_id}/",
          { params: { path: { id: groupId, membership_id: membershipId } } },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formationGroupKeys.detail(groupId) });
      qc.invalidateQueries({ queryKey: formationGroupKeys.list() });
    },
  });
}

/**
 * The goal ids a new group arrives prefilled with.
 *
 * Read from the training groups endpoint, not from the goal catalogue: which
 * goals the CQDD designated for demonstration is back-office configuration,
 * and serving it from `/questionnaire/goals/` would have handed it to every
 * authenticated account. Administrator-only here, like its Django twin.
 */
export function useDefaultGoalsQuery() {
  return useQuery({
    queryKey: formationGroupKeys.defaultGoals(),
    queryFn: async () => {
      const data = (await unwrap(
        api.GET("/api/v1/formation-groups/default-goals/"),
      )) as { goals: string[] };
      return data.goals;
    },
  });
}

/**
 * The questionnaire catalogue the goal picker nests.
 *
 * These three endpoints scope themselves through `content_scope`, which is
 * unrestricted for an administrator — so the picker sees the whole shared
 * catalogue, and a member-authored goal is not in it to begin with.
 */
export function useGoalCatalogueQuery() {
  return useQuery({
    queryKey: formationGroupKeys.catalogue(),
    queryFn: async () => {
      const [thematics, issues, goals] = await Promise.all([
        unwrap(api.GET("/api/v1/questionnaire/thematics/")) as Promise<
          Thematic[]
        >,
        unwrap(api.GET("/api/v1/questionnaire/issues/")) as Promise<Issue[]>,
        unwrap(api.GET("/api/v1/questionnaire/goals/")) as Promise<Goal[]>,
      ]);
      return { thematics, issues, goals };
    },
  });
}
